import Button from '@betterfunnel/ui-core/Button';
import FullScreenModal from '@betterfunnel/ui-core/FullScreenModal';
import IconButton from '@betterfunnel/ui-core/IconButton';
import MenuButton from '@betterfunnel/ui-core/MenuButton';
import Modal from '@betterfunnel/ui-core/Modal';
import TextField from '@betterfunnel/ui-core/TextField';
import { AdsCampaign, DynamicPage, LinkedCampaign } from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import {
    MagnifyingGlassIcon,
    PlusIcon,
    SparklesIcon,
    XMarkIcon,
} from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { collection, doc } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import googleAdsIcon from '../assets/images/googleAdsIcon.png';
import AdsAccountTree from '../components/AdsAccountTree/AdsAccountTree';
import DashboardAnalyticsRangeSelector from '../components/DashboardAnalyticsRangeSelector';
import {
    DynamicPagesTable,
    DynamicPageTableItem,
} from '../components/DynamicPagesTable';
import {
    FilterButton,
    FilterButtonOption,
} from '../components/FilterButton/FilterButton';
import { AuthContext } from '../contexts/AuthContext';
import { DashboardContext } from '../contexts/DashboardContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import { db } from '../Firebase';
import { useHasFeature } from '../hooks/useHasFeature';
import { getCampaigns } from '../services/AdsAccountsService';
import {
    createLinkedCampaign,
    deleteLinkedCampaign,
} from '../services/LinkedCampaignsService';
import { analyticsRangeToDates } from '../utils';
import { track } from '../utils/analytics';

interface SearchCampaignsFilters {
  adAccounts?: string[];
}

const AnimateStat = () => (
  <div className="h-2.5 w-1/2 min-w-7 max-w-12 animate-pulse rounded-md bg-gray-300"></div>
);

interface EnrichedLinkedCampaign extends LinkedCampaign {
  id: string;
  dynamicPage?: DynamicPage;
  adsCampaign?: AdsCampaign;
}

export default function SearchCampaigns() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasFinancialDataFeature = useHasFeature('viewFinancialData');

  const { show, hide } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPages, linkedCampaigns, analyticsRange, adsAccounts } =
    useContext(DashboardContext);
  const [adsCampaigns, setAdsCampaigns] = useState<AdsCampaign[]>();
  const [openAdsAccountTreeDialog, setOpenAdsAccountTreeDialog] = useState<{
    requestId: string;
    adsAccountId: string;
    campaignId: string;
  }>();
  const [openOptimizeCampaignDialog, setOpenOptimizeCampaignDialog] = useState<{
    campaignId: string;
  }>();
  const [filter, setFilter] = useState<SearchCampaignsFilters>({});

  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('search') ?? '',
  );
  const [_searchFieldFocused, setSearchFieldFocused] = useState(false);

  useEffect(() => {
    setSearchParams((params) => {
      if (searchQuery) {
        params.set('search', searchQuery);
      } else {
        params.delete('search');
      }

      return new URLSearchParams(params);
    });
  }, [searchQuery, setSearchParams]);

  useEffect(() => {
    if (!selectedAccount) {
      setAdsCampaigns(undefined);
      return;
    }

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);

    getCampaigns(selectedAccount?.id, startDate, endDate)
      .then((res) => {
        setAdsCampaigns(res);
      })
      .catch((e) => {
        Sentry.captureException(e);
        show(e.message, 'warn');
      });
  }, [selectedAccount, analyticsRange, show]);

  const filteredLinkedCampaigns: EnrichedLinkedCampaign[] | undefined =
    useMemo(() => {
      const filtered = linkedCampaigns
        ?.map((lc): EnrichedLinkedCampaign => {
          const dynamicPage = dynamicPages
            ?.find((p) => p.id === lc.data().dynamicPageId)
            ?.data();
          const adsCampaign = adsCampaigns?.find(
            (c) => c.id === lc.data().externalCampaignId,
          );
          return {
            ...lc.data(),
            id: lc.id,
            name: adsCampaign?.name ?? lc.data().name,
            dynamicPage,
            adsCampaign,
          };
        })
        ?.filter((enrichedLinkedCampaign) => {
          if (searchQuery && searchQuery.length > 2) {
            if (
              !enrichedLinkedCampaign.name
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
            ) {
              return false;
            }
          }

          if (filter.adAccounts && filter.adAccounts.length > 0) {
            if (
              !filter.adAccounts.includes(
                enrichedLinkedCampaign.externalAdsAccountId,
              )
            ) {
              return false;
            }
          }

          return true;
        });

      return filtered;
    }, [
      linkedCampaigns,
      dynamicPages,
      adsCampaigns,
      searchQuery,
      filter.adAccounts,
    ]);

  const filteredAdsCampaigns = useMemo(() => {
    if (!adsCampaigns) return undefined;
    if (!linkedCampaigns) return undefined;

    const linkedCampaignsIdsSet = new Set(
      linkedCampaigns.map((lc) => lc.data().externalCampaignId),
    );
    return adsCampaigns
      .filter((adsCampaign) => {
        return !linkedCampaignsIdsSet.has(adsCampaign.id);
      })
      .filter((adsCampaign) => {
        return adsCampaign.metrics?.clicks ?? 0 > 0;
      })
      .filter((adsCampaign) => {
        if (searchQuery && searchQuery.length > 2) {
          if (
            !adsCampaign.name.toLowerCase().includes(searchQuery.toLowerCase())
          ) {
            return false;
          }
        }

        if (filter.adAccounts && filter.adAccounts.length > 0) {
          if (!filter.adAccounts.includes(adsCampaign.adsAccountId)) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        return (b.metrics?.cost ?? 0) - (a.metrics?.cost ?? 0);
      });
  }, [adsCampaigns, linkedCampaigns, searchQuery, filter.adAccounts]);

  return (
    <>
      <FullScreenModal open={!!openAdsAccountTreeDialog} onClose={() => {}}>
        <div className="h-full px-4 pb-4 pt-5 sm:p-6">
          <IconButton
            variant="ternary"
            onClick={() => {
              setOpenAdsAccountTreeDialog(undefined);
            }}
            className="fixed left-6 top-6 z-50"
          >
            <XMarkIcon className="h-6 w-6" />
          </IconButton>
          <div className="absolute -bottom-2 -left-2 -right-2 -top-2">
            {openAdsAccountTreeDialog ? (
              <AdsAccountTree
                requestId={openAdsAccountTreeDialog.requestId}
                adsAccountId={openAdsAccountTreeDialog.adsAccountId}
                campaignIds={openAdsAccountTreeDialog.campaignId}
              />
            ) : (
              <></>
            )}
          </div>
        </div>
      </FullScreenModal>
      <SelectDynamicPageModal
        open={!!openOptimizeCampaignDialog}
        onClose={() => setOpenOptimizeCampaignDialog(undefined)}
        adsCampaign={adsCampaigns?.find(
          (c) => c.id === openOptimizeCampaignDialog?.campaignId,
        )}
      />
      <div className="flex h-full w-full flex-col">
        <div className="flex flex-col gap-4 bg-white">
          <div className="flex flex-col items-stretch sm:flex-row sm:items-center">
            <div className="flex h-12 w-full flex-row items-center justify-between gap-2 border-b border-gray-300 pb-3">
              <h1 className="w-full grow text-2xl font-semibold leading-6 text-gray-900">
                Search Campaigns
              </h1>
              <div className="flex flex-row gap-2">
                <Button
                  className="shrink-0 whitespace-nowrap"
                  onClick={() => {
                    track('new_campaign_button_click');
                    navigate(`/workspace/${slug}/settings/ad-accounts`);
                  }}
                  icon={<PlusIcon className="h-4 w-4" />}
                >
                  New Ad Account
                </Button>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 lg:flex-row lg:justify-between lg:gap-0">
            <div className="flex flex-row justify-between gap-4">
              <TextField
                startIcon={
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
                }
                fullWidth
                className="h-8 w-full lg:w-52"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                placeholder="Search"
                onFocus={() => setSearchFieldFocused(true)}
                onBlur={() => setSearchFieldFocused(false)}
              />
              <div className="shrink-0">
                <FilterButton>
                  <FilterButtonOption
                    title="Ad Acount"
                    selected={filter.adAccounts ?? []}
                    options={adsAccounts ?? []}
                    getOptionKey={(option) => option.id}
                    getOptionLabel={(option) =>
                      `${option.data().name} - ${option.id}`
                    }
                    onChange={(selected) => {
                      setFilter({ ...filter, adAccounts: selected });
                    }}
                  />
                </FilterButton>
              </div>
            </div>
            <div>
              <DashboardAnalyticsRangeSelector size="small" variant="text" />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-scroll px-0 sm:px-0">
          <table className="min-w-full">
            <thead className="sticky top-0 border-b border-gray-300 bg-white">
              <tr>
                <th
                  scope="col"
                  className="py-3.5 pr-3 ps-2 text-left text-sm font-semibold text-gray-900"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Platform
                </th>
                {hasFinancialDataFeature && (
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Cost
                  </th>
                )}
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Clicks
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Conversions
                </th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                  <span className="sr-only">Edit</span>
                </th>
              </tr>
            </thead>
            <tbody className="w-full divide-y divide-gray-200">
              <tr className="sticky top-12 bg-white">
                <td
                  colSpan={6}
                  className="bg-gray-50 py-3.5 pr-3 ps-2 text-left text-sm font-semibold text-gray-900"
                >
                  Optimized Campaigns
                </td>
              </tr>
              {filteredLinkedCampaigns?.map((linkedCampaignData) => {
                const adsAccount = adsAccounts?.find(
                  (a) => a.id === linkedCampaignData.externalAdsAccountId,
                );
                const currencyFormatter = new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: adsAccount?.data().currencyCode ?? 'USD',
                });

                const formatter = new Intl.NumberFormat('en-US', {
                  maximumFractionDigits: 2,
                });

                // const timeframe = analyticsRange;
                // const isCustomTimeframe = typeof timeframe !== 'string';

                // const _analyticsData = isCustomTimeframe
                //   ? customRangeAnalytics
                //   : analytics?.data();

                // const analyticsData = _analyticsData
                //   ? (_analyticsData?.campaign?.[
                //       linkedCampaignData.dynamicPageId
                //     ] ?? {})
                //   : undefined;

                // const analyticsKey = isCustomTimeframe
                //   ? 'custom'
                //   : timeframe;

                // const dynamicPageExtra = getDynamicPageAttributes(
                //   linkedCampaignData.dynamicPageId,
                //   linkedCampaignData.dynamicPage,
                //   analyticsRange,
                //   analytics,
                //   customRangeAnalytics,
                // );

                return (
                  <tr key={linkedCampaignData.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                      <div className="text-gray-900">
                        {linkedCampaignData.name}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                      <div className="text-gray-900">
                        {linkedCampaignData.externalCampaignProvider ===
                        'google' ? (
                          <img
                            className="h-5 w-auto"
                            src={googleAdsIcon}
                            alt={'Google Ads'}
                          />
                        ) : (
                          <div>
                            {linkedCampaignData.externalCampaignProvider}
                          </div>
                        )}
                      </div>
                    </td>
                    {hasFinancialDataFeature && (
                      <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                        {linkedCampaignData.adsCampaign ? (
                          <div className="text-gray-900">
                            {currencyFormatter.format(
                              linkedCampaignData.adsCampaign.metrics?.cost ?? 0,
                            )}
                          </div>
                        ) : (
                          <AnimateStat />
                        )}
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                      {linkedCampaignData.adsCampaign ? (
                        <div className="text-gray-900">
                          {formatter.format(
                            linkedCampaignData.adsCampaign.metrics?.clicks ?? 0,
                          )}
                        </div>
                      ) : (
                        <AnimateStat />
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                      {linkedCampaignData.adsCampaign ? (
                        <div className="text-gray-900">
                          {formatter.format(
                            linkedCampaignData.adsCampaign.metrics
                              ?.conversions ?? 0,
                          )}
                        </div>
                      ) : (
                        <AnimateStat />
                      )}
                    </td>
                    <td className="relative flex flex-row items-center justify-end gap-1 whitespace-nowrap py-5 pe-5 pl-3 text-right text-sm font-medium">
                      {linkedCampaignData.dynamicPageId ? (
                        <Button
                          variant="secondary"
                          color="primary"
                          onClick={() => {
                            navigate(
                              `/workspace/${slug}/pages/${linkedCampaignData.dynamicPageId}`,
                            );
                          }}
                        >
                          Dynamic Page
                        </Button>
                      ) : (
                        <></>
                      )}
                      <MenuButton
                        items={[
                          {
                            title: 'Open in new tab',
                            onClick: () => {
                              window.open(
                                `/workspace/${slug}/pages/${linkedCampaignData.dynamicPageId}`,
                                '_blank',
                              );
                            },
                          },
                          {
                            title: 'Unlink Dynamic Page',
                            destructive: true,
                            onClick: () => {
                              const sb = show('Unlinking Campaign', 'info', {
                                disableAutoClose: true,
                              });
                              deleteLinkedCampaign(
                                selectedAccount?.id ?? '',
                                linkedCampaignData.id,
                              )
                                .then(() => {
                                  show('Unlinked Campaign');
                                })
                                .catch((e) => {
                                  console.error(e);
                                  Sentry.captureException(e);
                                  show('Failed to unlink campaign', 'warn');
                                })
                                .finally(() => {
                                  hide(sb.id);
                                });
                            },
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              }) ?? []}

              {!filteredLinkedCampaigns &&
                new Array(5)
                  .fill(0)
                  .map((_, i) => i)
                  .map((v) => (
                    <tr key={v}>
                      {new Array(4)
                        .fill(0)
                        .map((_, i) => i)
                        .map((v) => (
                          <td
                            key={v}
                            className="whitespace-nowrap px-3 py-5 text-sm text-gray-500"
                          >
                            <AnimateStat />
                          </td>
                        ))}
                    </tr>
                  ))}

              {filteredLinkedCampaigns?.length === 0 ? (
                <tr className="h-24">
                  <td
                    colSpan={6}
                    className="flex whitespace-nowrap text-sm text-gray-500"
                  >
                    <span className="px-3 py-5">No Linked Campaigns</span>
                  </td>
                </tr>
              ) : (
                <tr className="h-12">
                  <td></td>
                </tr>
              )}

              <tr className="sticky top-12 w-full bg-white">
                <td
                  colSpan={6}
                  className="bg-gray-50 py-3.5 pr-3 ps-2 text-left text-sm font-semibold text-gray-900"
                >
                  Suggested Campaigns
                </td>
              </tr>
              {filteredAdsCampaigns?.map((adsCampaign) => {
                const adsAccount = adsAccounts?.find(
                  (a) => a.id === adsCampaign.adsAccountId,
                );

                const currencyFormatter = new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: adsAccount?.data().currencyCode ?? 'USD',
                });

                const formatter = new Intl.NumberFormat('en-US', {
                  maximumFractionDigits: 2,
                });

                return (
                  <tr key={adsCampaign.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                      <div className="text-gray-900">{adsCampaign.name}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                      <img
                        className="h-5 w-auto"
                        src={googleAdsIcon}
                        alt={'Google Ads'}
                      />
                    </td>
                    {hasFinancialDataFeature && (
                      <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                        <div className="text-gray-900">
                          {currencyFormatter.format(
                            adsCampaign.metrics?.cost ?? 0,
                          )}
                        </div>
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                      <div className="text-gray-900">
                        {formatter.format(adsCampaign.metrics?.clicks ?? 0)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                      <div className="text-gray-900">
                        {formatter.format(
                          adsCampaign.metrics?.conversions ?? 0,
                        )}
                      </div>
                    </td>
                    <td className="flex flex-row justify-end gap-2 whitespace-nowrap py-5 pe-5 pl-3 text-right text-sm font-medium">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          const requestId = doc(collection(db, 'asdasdsa')).id;
                          setOpenAdsAccountTreeDialog({
                            requestId: requestId,
                            adsAccountId: adsCampaign.adsAccountId,
                            campaignId: adsCampaign.id,
                          });
                        }}
                      >
                        Ad Map
                      </Button>
                      <Button
                        icon={<SparklesIcon className="h-4 w-4" />}
                        onClick={() => {
                          setOpenOptimizeCampaignDialog({
                            campaignId: adsCampaign.id,
                          });
                        }}
                      >
                        Optimize
                      </Button>
                    </td>
                  </tr>
                );
              }) ?? []}
              {!filteredAdsCampaigns &&
                new Array(5)
                  .fill(0)
                  .map((_, i) => i)
                  .map((v) => (
                    <tr key={v}>
                      {new Array(4)
                        .fill(0)
                        .map((_, i) => i)
                        .map((v) => (
                          <td
                            key={v}
                            className="whitespace-nowrap px-3 py-5 text-sm text-gray-500"
                          >
                            <AnimateStat />
                          </td>
                        ))}
                    </tr>
                  ))}
              {filteredAdsCampaigns?.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="flex whitespace-nowrap text-sm text-gray-500"
                  >
                    <span className="px-3 py-5">No Suggested Campaigns</span>
                  </td>
                </tr>
              ) : (
                <></>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

interface SelectDynamicPageModalProps {
  open: boolean;
  onClose: () => void;
  adsCampaign: AdsCampaign | undefined;
}

const SelectDynamicPageModal = ({
  open,
  onClose,
  adsCampaign,
}: SelectDynamicPageModalProps) => {
  const { show } = useContext(SnackbarContext);
  const { firebaseUser } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPages } = useContext(DashboardContext);

  const onLinkButtonClick = useCallback(
    (dynamicPage: DynamicPageTableItem) => {
      if (!selectedAccount || !firebaseUser || !adsCampaign) return;

      createLinkedCampaign(
        selectedAccount.id,
        adsCampaign,
        firebaseUser.uid,
        dynamicPage.id,
      )
        .then(() => {
          show('Linked Campaign');
        })
        .catch((e) => {
          console.error(e);
          Sentry.captureException(e);
          show('Failed to optimize campaign', 'warn');
        });
    },
    [adsCampaign, firebaseUser, selectedAccount, show],
  );
  return (
    <Modal actions={[]} open={open} onClose={onClose} width="4xl">
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Select a Dynamic Page to link
      </DialogTitle>
      <div className="-mx-6" style={{ height: 600 }}>
        <DynamicPagesTable
          hideAnalytics
          items={dynamicPages}
          actions={(dynamicPage) => {
            return (
              <Button
                variant="secondary"
                onClick={() => {
                  onLinkButtonClick(dynamicPage);
                  onClose();
                }}
              >
                Link
              </Button>
            );
          }}
        />
      </div>
    </Modal>
  );
};
